(() => {
  'use strict';
  const initialize = (scope = document) => {
    const header = scope.querySelector('[data-pj-studio-header]');
    if (header && !header.dataset.initialized) {
      header.dataset.initialized = 'true';
      const updateHeaderHeight = () => {
        const height = Math.round(header.getBoundingClientRect().height);
        ['--pj-header-height', '--header-height', '--header-height-sticky'].forEach(property => document.documentElement.style.setProperty(property, `${height}px`));
      };
      new ResizeObserver(updateHeaderHeight).observe(header);
      updateHeaderHeight();
      const menus = Array.from(header.querySelectorAll('[data-pj-desktop-menu]'));
      const closeMenus = (except) => menus.forEach(menu => { if (menu !== except) menu.open = false; });
      menus.forEach(menu => {
        let closing;
        let opening;
        menu.querySelector('summary').addEventListener('click', () => clearTimeout(opening));
        menu.addEventListener('toggle', () => { if (menu.open) closeMenus(menu); });
        menu.addEventListener('pointerenter', (event) => {
          if (event.pointerType !== 'mouse') return;
          clearTimeout(closing);
          opening = setTimeout(() => {
            closeMenus(menu);
            menu.open = true;
          }, 120);
        });
        menu.addEventListener('pointerleave', (event) => {
          if (event.pointerType !== 'mouse') return;
          clearTimeout(opening);
          closing = setTimeout(() => { if (!menu.contains(document.activeElement)) menu.open = false; }, 160);
        });
        menu.addEventListener('keydown', event => {
          if (event.key === 'Escape') { menu.open = false; menu.querySelector('summary').focus(); }
        });
        menu.addEventListener('focusout', () => setTimeout(() => { if (!menu.contains(document.activeElement)) menu.open = false; }, 0));
      });
      document.addEventListener('click', event => { if (!header.contains(event.target)) closeMenus(); });
    }

    scope.querySelectorAll('[data-pj-menu-dialog], [data-pj-search-dialog]').forEach(dialog => {
      if (dialog.dataset.initialized) return;
      dialog.dataset.initialized = 'true';
      const triggerSelector = dialog.matches('[data-pj-menu-dialog]') ? '[data-pj-menu-open]' : '[data-pj-search-open]';
      const triggers = Array.from(document.querySelectorAll(`${triggerSelector}[aria-controls="${dialog.id}"]`));
      triggers.forEach(trigger => trigger.addEventListener('click', () => {
        dialog.showModal();
        trigger.setAttribute('aria-expanded', 'true');
        document.body.classList.add('pj-studio-dialog-open');
        dialog.querySelector('input[type="search"]')?.focus();
      }));
      dialog.querySelectorAll('[data-pj-dialog-close]').forEach(button => button.addEventListener('click', () => dialog.close()));
      dialog.addEventListener('click', event => {
        if (event.target !== dialog) return;
        const bounds = dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) dialog.close();
      });
      dialog.addEventListener('close', () => {
        triggers.forEach(trigger => trigger.setAttribute('aria-expanded', 'false'));
        if (!document.querySelector('.pj-studio-mobile-menu[open], .pj-studio-search-dialog[open]')) document.body.classList.remove('pj-studio-dialog-open');
      });
    });

    scope.querySelectorAll('[data-pj-predictive-search]').forEach(form => {
      if (form.dataset.initialized) return;
      form.dataset.initialized = 'true';
      const input = form.querySelector('input[name="q"]');
      const results = form.querySelector('[data-pj-search-results]');
      const status = form.querySelector('[data-pj-search-status]');
      let timer;
      let controller;
      let activeIndex = -1;
      let currentQuery = '';
      const close = () => {
        results.hidden = true;
        input.setAttribute('aria-expanded', 'false');
        input.removeAttribute('aria-activedescendant');
        activeIndex = -1;
      };
      const safeURL = (url) => {
        try {
          const parsed = new URL(url, window.location.origin);
          if (parsed.origin === window.location.origin) return parsed.href;
          if (parsed.hostname === form.dataset.shopDomain) return new URL(`${parsed.pathname}${parsed.search}${parsed.hash}`, window.location.origin).href;
          return '';
        } catch { return ''; }
      };
      const makeResult = (item, type, index) => {
        const href = safeURL(item.url);
        if (!href) return null;
        const link = document.createElement('a');
        link.href = href;
        link.className = 'pj-studio-search__result';
        link.id = `${results.id}-option-${index}`;
        link.setAttribute('role', 'option');
        link.setAttribute('aria-selected', 'false');
        link.tabIndex = -1;
        if (type === 'products' && item.image) {
          const source = typeof item.image === 'object' ? item.image.src : item.image;
          if (source) {
            const img = document.createElement('img');
            img.src = source;
            img.alt = '';
            img.width = 60;
            img.height = 60;
            img.loading = 'lazy';
            link.append(img);
          }
        }
        const title = document.createElement('span');
        title.textContent = item.title;
        link.append(title);
        return link;
      };
      const renderResults = (data, query) => {
        results.replaceChildren();
        const resourceResults = data.resources?.results || {};
        let count = 0;
        ['products', 'collections', 'pages', 'articles'].forEach(type => {
          const items = resourceResults[type] || [];
          if (!items.length) return;
          const heading = document.createElement('p');
          heading.className = 'pj-studio-search__group-title';
          heading.textContent = {products: 'Products', collections: 'Collections', pages: 'Pages', articles: 'Journal'}[type];
          results.append(heading);
          const list = document.createElement('div');
          list.setAttribute('role', 'listbox');
          list.setAttribute('aria-label', heading.textContent);
          items.forEach(item => {
            const result = makeResult(item, type, count);
            if (result) { list.append(result); count += 1; }
          });
          results.append(list);
        });
        if (!count) {
          const message = document.createElement('p');
          message.className = 'pj-studio-search__message';
          message.textContent = `No suggestions for “${query}”. Try the full search below.`;
          results.append(message);
        }
        const all = document.createElement('a');
        const searchURL = new URL(form.action, window.location.origin);
        searchURL.searchParams.set('q', query);
        searchURL.searchParams.set('options[prefix]', 'last');
        all.href = searchURL.href;
        all.className = 'pj-studio-search__all';
        all.textContent = `Search all results for “${query}” ↗`;
        results.append(all);
        results.hidden = false;
        input.setAttribute('aria-expanded', 'true');
        input.removeAttribute('aria-activedescendant');
        activeIndex = -1;
        status.textContent = `${count} suggestions found. Use the arrow keys to explore.`;
      };
      const fetchResults = async () => {
        const query = input.value.trim();
        if (query.length < 2) { controller?.abort(); close(); return; }
        controller?.abort();
        controller = new AbortController();
        currentQuery = query;
        const endpoint = new URL(`${form.dataset.suggestUrl}.json`, window.location.origin);
        endpoint.searchParams.set('q', query);
        endpoint.searchParams.set('resources[type]', 'product,collection,page,article');
        endpoint.searchParams.set('resources[limit]', '4');
        endpoint.searchParams.set('resources[options][unavailable_products]', 'last');
        status.textContent = 'Searching…';
        try {
          const response = await fetch(endpoint, {signal: controller.signal, headers: {'Accept': 'application/json'}});
          if (!response.ok) throw new Error('Predictive search unavailable');
          const data = await response.json();
          if (input.value.trim() === query) renderResults(data, query);
        } catch (error) {
          if (error.name === 'AbortError') return;
          close();
          status.textContent = 'Press Enter to search all products and pages.';
        }
      };
      input.addEventListener('input', () => { clearTimeout(timer); timer = setTimeout(fetchResults, 200); });
      input.addEventListener('focus', () => { if (input.value.trim() === currentQuery && results.childElementCount) { results.hidden = false; input.setAttribute('aria-expanded', 'true'); } });
      input.addEventListener('keydown', event => {
        if (event.key === 'Escape') { close(); return; }
        if (results.hidden) return;
        const options = Array.from(results.querySelectorAll('[role="option"]'));
        if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && options.length) {
          event.preventDefault();
          activeIndex = event.key === 'ArrowDown' ? (activeIndex + 1) % options.length : (activeIndex - 1 + options.length) % options.length;
          options.forEach((option, index) => option.setAttribute('aria-selected', String(index === activeIndex)));
          input.setAttribute('aria-activedescendant', options[activeIndex].id);
          options[activeIndex].scrollIntoView({block: 'nearest'});
        } else if (event.key === 'Enter' && activeIndex >= 0 && options[activeIndex]) {
          event.preventDefault();
          window.location.assign(options[activeIndex].href);
        }
      });
      form.addEventListener('focusout', () => setTimeout(() => { if (!form.contains(document.activeElement)) close(); }, 120));
      document.addEventListener('click', event => { if (!form.contains(event.target)) close(); });
    });

    scope.querySelectorAll('[data-pj-budget-feature]').forEach(feature => {
      if (feature.dataset.initialized) return;
      feature.dataset.initialized = 'true';
      const form = feature.querySelector('form[is="product-form"]');
      const choices = Array.from(feature.querySelectorAll('[data-pj-budget-choice]'));
      const variantId = feature.querySelector('[data-pj-budget-variant-id]');
      const addButton = feature.querySelector('[data-pj-budget-add]');
      const price = feature.querySelector('[data-pj-budget-price]');
      const image = feature.querySelector('.pj-studio-budget__photograph img');
      const imageNote = feature.querySelector('[data-pj-budget-image-note]');
      const batchNotice = feature.querySelector('[data-pj-budget-batch-notice]');
      const status = feature.querySelector('[data-pj-budget-status]');
      if (!form || !variantId || !addButton) return;

      // This product requires an intentional strain choice; never seed the form
      // from the first available variant or a restored browser field value.
      choices.forEach(choice => { choice.checked = false; });
      variantId.value = '';
      variantId.disabled = true;
      addButton.disabled = true;
      addButton.setAttribute('aria-disabled', 'true');

      const selectChoice = (choice) => {
        if (!choice.checked) return;
        variantId.value = choice.value;
        variantId.disabled = false;
        price.textContent = choice.dataset.price;
        if (image && choice.dataset.imageSrc) {
          image.src = choice.dataset.imageSrc;
          image.srcset = choice.dataset.imageSrcset || '';
          image.alt = choice.dataset.imageAlt || choice.dataset.title;
          if (choice.dataset.imageWidth) image.width = Number(choice.dataset.imageWidth);
          if (choice.dataset.imageHeight) image.height = Number(choice.dataset.imageHeight);
        }
        feature.querySelectorAll('[data-pj-budget-product-link]').forEach(link => { link.href = choice.dataset.url; });
        if (imageNote) imageNote.textContent = `${choice.dataset.title} / Current selection`;
        if (batchNotice) {
          batchNotice.textContent = choice.dataset.batchNotice || '';
          batchNotice.hidden = !batchNotice.textContent;
        }
        addButton.disabled = false;
        addButton.removeAttribute('aria-disabled');
        addButton.setAttribute('aria-label', `Add 1 ounce of ${choice.dataset.title} to cart`);
        const label = addButton.querySelector('[data-part="label"]');
        if (label) label.textContent = `Add to cart — ${choice.dataset.price}`;
        if (status) status.textContent = `${choice.dataset.title} selected. ${choice.dataset.price} for 1 ounce. ${choice.dataset.batchNotice || ''}`;
      };

      choices.forEach(choice => {
        choice.addEventListener('change', () => selectChoice(choice));
        choice.addEventListener('keydown', event => {
          const visible = choices.filter(item => !item.closest('li').hidden);
          const current = visible.indexOf(choice);
          let next;
          if (event.key === 'Home') next = visible[0];
          if (event.key === 'End') next = visible[visible.length - 1];
          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = visible[(current + 1) % visible.length];
          if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = visible[(current - 1 + visible.length) % visible.length];
          if (next) { event.preventDefault(); next.checked = true; next.focus(); selectChoice(next); }
        });
      });
      feature.querySelector('[data-pj-budget-show-all]')?.addEventListener('click', event => {
        const extra = Array.from(feature.querySelectorAll('[data-pj-budget-extra-choice]'));
        extra.forEach(item => { item.hidden = false; });
        event.currentTarget.hidden = true;
        extra[0]?.querySelector('input')?.focus();
      });
      form.addEventListener('submit', event => {
        const selected = choices.find(choice => choice.checked);
        if (!selected || variantId.disabled || variantId.value !== selected.value) {
          event.preventDefault();
          event.stopImmediatePropagation();
          if (status) status.textContent = 'Choose a strain before adding to cart.';
          choices.find(choice => !choice.closest('li').hidden)?.focus();
        }
      }, {capture: true});
    });

    scope.querySelectorAll('[data-pj-discovery]').forEach(discovery => {
      if (discovery.dataset.initialized) return;
      discovery.dataset.initialized = 'true';
      const tabs = Array.from(discovery.querySelectorAll('[data-pj-family-tab]'));
      const activate = (tab, focus) => {
        tabs.forEach(item => {
          const selected = item === tab;
          item.setAttribute('aria-selected', String(selected));
          item.tabIndex = selected ? 0 : -1;
          const panel = discovery.querySelector(`#${item.getAttribute('aria-controls')}`);
          if (panel) panel.hidden = !selected;
        });
        if (focus) tab.focus();
      };
      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => activate(tab, false));
        tab.addEventListener('keydown', event => {
          let target;
          if (event.key === 'ArrowRight') target = tabs[(index + 1) % tabs.length];
          if (event.key === 'ArrowLeft') target = tabs[(index - 1 + tabs.length) % tabs.length];
          if (event.key === 'Home') target = tabs[0];
          if (event.key === 'End') target = tabs[tabs.length - 1];
          if (target) { event.preventDefault(); activate(target, true); }
        });
      });
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize());
  else initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
})();
